import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Caption from 'LuxKit/Typography/Caption'
import GeoContext from 'contexts/geoContext'
import type { AccommodationItem } from 'tripPlanner/types/tripItem'
import { findNearestItem, formatDistance } from 'tripPlanner/utils/geo'

const StyledCaptionTextBlock = styled(BodyTextBlock)`
  color: ${(props) => props.theme.palette.highlight.secondary.normalForeground};
`

interface Props {
  secondaryText?: string
  location?: { lat?: number, lng?: number, placeId: string }
  accommodationItems: Array<AccommodationItem>
}

function MapSearchPresentationSelector(props: Props) {
  const { secondaryText, location, accommodationItems } = props
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)

  // There are only ever five results at a time.
  // So the time complexity of this is only 5 * number of hotels/airbnbs in the current trip
  // i.e. O(n) (linear time) where n is number of hotels/airbnbs
  const nearestAccommodation = useMemo(() => {
    if (!location?.lat || !location?.lng) return undefined

    if (!accommodationItems.length) return undefined

    const { lat: predLat, lng: predLng } = location
    const nearestAccommodationResult = findNearestItem(accommodationItems, predLat, predLng)

    if (!nearestAccommodationResult) return undefined

    const distance = nearestAccommodationResult.distance

    // Omit the distance if prediction is the nearest accommodation itself.
    if (nearestAccommodationResult.place.id === location.placeId) {
      return undefined
    }

    if (distance > 100) return undefined

    return {
      distance: formatDistance(distance, currentRegionCode),
      name: nearestAccommodationResult.item.sortableName,
    }
  }, [accommodationItems, currentRegionCode, location])

  return (
    <div>
      <Caption variant="large">{secondaryText}</Caption>
      {!!nearestAccommodation && (
        <StyledCaptionTextBlock variant="small" weight="bold">
          {intl.formatMessage(
            {
              id: 'Lxl1XZ2Lb3GsEjr2MwTN',
              defaultMessage: '{distance} from {accommodationName}',
              description: 'Distance label showing how far a location is from the nearest accommodation',
            },
            { distance: nearestAccommodation.distance, accommodationName: nearestAccommodation.name },
          )}
        </StyledCaptionTextBlock>
      )}
    </div>
  )
}

export default MapSearchPresentationSelector
